import { Component, inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { IProductDataV3 } from '@df/catalog/product/product';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Product } from '../../../../product.decorator';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { BehaviorSubject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { App } from '@df/core/app';

export namespace NProductViewDetailsQuoteAttributes {
    export const BLOCK_ID = 'cms-product-view-details-quote-attributes';

    export type IBlockData = Catalog.IBlockData<IBlockDataContent>;

    export interface IBlockDataContent {
        attributes: string[];
    }

    export interface IItem {
        label: string;
        options: string | number;
    }
}

@ICmsComponent<ProductViewDetailsQuoteAttributesComponent>({
    id: NProductViewDetailsQuoteAttributes.BLOCK_ID,
    block: {
        url: NProductViewDetailsQuoteAttributes.BLOCK_ID
    },
    onDemandOnly: true,
    name: 'Product page quote attributes',
    description: 'Product page quote attributes block',
    fields: [
        <NICmsContentField.IRelationField>{
            key: 'attributes',
            type: NICmsContentField.EFieldType.relation,
            title: 'Attributes',
            options: {
                type: 'attribute',
                multiple: true
            }
        }
    ]
})
@Component({
    standalone: true,
    imports: [TranslationModule, CommonModule],
    selector: 'product-view-details-quote-attributes',
    templateUrl: './product-view-details-quote-attributes.html'
})
export class ProductViewDetailsQuoteAttributesComponent
    extends UiComponent
    implements NICmsContentComponent.IComponent, OnInit, OnChanges, OnDestroy
{
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NProductViewDetailsQuoteAttributes.IBlockDataContent;

    @Input({
        required: true
    })
    product!: Product<IProductDataV3>;

    _icmsContent$!: BehaviorSubject<any>;

    items: NProductViewDetailsQuoteAttributes.IItem[] = [];

    private app = inject(App);

    override ngOnInit() {
        super.ngOnInit();

        if (this._icmsContent$) {
            this.subscriptions.add(
                this._icmsContent$.subscribe(() => {
                    this.updateItems();
                })
            );
        }
    }

    override ngOnChanges(changes: any): void {
        this.updateItems();
    }

    private updateItems(): void {
        const attributes = this.getAttributes();
        const items: NProductViewDetailsQuoteAttributes.IItem[] = [];
        if (this.product && attributes.length) {
            attributes.forEach(attributeCode => {
                const attributeModel = this.app.resources.attribute.getExisting(attributeCode);
                if (attributeModel?.data) {
                    const value = this.product.data?.[attributeCode];
                    if (value) {
                        const optionsLabel = attributeModel.getOptionLabel(value);
                        if (optionsLabel) {
                            items.push({
                                label: attributeModel.data.label,
                                options: Array.isArray(optionsLabel) ? optionsLabel.join(', ') : optionsLabel
                            });
                        }
                    }
                }
            });
        }
        this.items = items;
        this.detectChanges();
    }

    protected get isIcms(): boolean {
        return this.client.isCms;
    }

    private getAttributes(): string[] {
        return this.content?.attributes || [];
    }
}
